import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { getConfig } from '../../config';
import './StripePayment.css';
import Text from '../Text/Text';
import { apiClient } from '../../api/client';

interface StripePaymentProps {
  amount: string;
  originalAmount: string;
  platformFee: string;
  publicUserId: string;  // Public user ID
  sellerId: string;      // Auth0 user ID (internal)
  itemId: string;
  promotionCode?: string;
  originalPlatformFee?: string;
  onReady: (isReady: boolean) => void;
  onPaymentProcessing: (isProcessing: boolean) => void;
  onPaymentError: (error: string) => void;
  onPaymentSuccess: (transactionId: string) => void;
}

const StripePaymentForm: React.FC<Omit<StripePaymentProps, 'amount'> & { clientSecret: string }> = ({
  clientSecret,
  onReady,
  onPaymentProcessing,
  onPaymentError,
  onPaymentSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!stripe || !elements) {
      onReady(false);
      return;
    }

    onReady(true);
  }, [stripe, elements, onReady]);

  const handleChange = (event: any) => {
    setIsComplete(event.complete);
    setError(event.error ? event.error.message : null);
    onReady(event.complete);
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return false;
    }

    onPaymentProcessing(true);
    setError(null);

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message || 'An error occurred during payment');
        onPaymentError(result.error.message || 'An error occurred during payment');
        onPaymentProcessing(false);
        return false;
      } else {
        // Payment succeeded
        try {
          console.log('Payment result:', result);
          const confirmation = await apiClient.payments.confirmPayment({
            paymentIntentId: result.paymentIntent.id
          });
          
          onPaymentSuccess(confirmation.transactionId);
          return true;
        } catch (error: any) {
          setError(error.message || 'Payment was processed but confirmation failed');
          onPaymentError(error.message || 'Payment was processed but confirmation failed');
          onPaymentProcessing(false);
          return false;
        }
      }
    } catch (error: any) {
      setError(error.message || 'An error occurred during payment');
      onPaymentError(error.message || 'An error occurred during payment');
      onPaymentProcessing(false);
      return false;
    }
  };

  return (
    <div className="stripe-payment-form">
      <PaymentElement onChange={handleChange} />
      {error && (
        <Text className="text-caption text-error">
          {error}
        </Text>
      )}
      <button
        className="stripe-submit-button"
        onClick={handleSubmit}
        disabled={!isComplete || !stripe || !elements}
      >
        <Text className="text-body text-white">
          Pay Now
        </Text>
      </button>
    </div>
  );
};

const StripePayment: React.FC<StripePaymentProps> = (props) => {
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const config = getConfig();
    const stripeKey = config.payment?.stripe?.publishableKey;
    
    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    } else {
      setError('Stripe publishable key not found in configuration');
      props.onReady(false);
    }
  }, []);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        setIsLoading(true);
        
        const response = await apiClient.payments.createPaymentIntent({
          totalAmount: parseFloat(props.amount),
          originalAmount: parseFloat(props.originalAmount),
          platformFee: parseFloat(props.platformFee),
          publicUserId: props.publicUserId,
          sellerId: props.sellerId,
          itemId: props.itemId,
          promotionCode: props.promotionCode
        });
        
        setClientSecret(response.clientSecret);
        setIsLoading(false);
      } catch (error: any) {
        setError(error.message || 'Failed to initialize payment');
        props.onPaymentError(error.message || 'Failed to initialize payment');
        setIsLoading(false);
      }
    };
    
    if (props.amount && parseFloat(props.amount) > 0) {
      createPaymentIntent();
    }
  }, [props.amount, props.originalAmount, props.platformFee, props.publicUserId, props.sellerId, props.itemId, props.promotionCode]);

  if (isLoading) {
    return <div className="stripe-loading">Initializing payment...</div>;
  }

  if (error) {
    return (
      <div className="stripe-error">
        <Text className="text-caption text-error">{error}</Text>
      </div>
    );
  }

  if (!clientSecret) {
    return <div className="stripe-error">Unable to initialize payment</div>;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0570de',
            colorBackground: '#ffffff',
            colorText: '#30313d',
            colorDanger: '#df1b41',
            fontFamily: 'Ideal Sans, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '4px',
          },
        },
      }}
    >
      <StripePaymentForm
        {...props}
        clientSecret={clientSecret}
      />
    </Elements>
  );
};

export default StripePayment;